import { useEffect, useState } from "react";
import type { NotificationKind } from "@/hooks/useNotifications";

/** Local-storage backed notification preferences. Kept client-side so we
 *  don't need a migration for what is purely a UI filter — useNotifications
 *  reads these flags and hides any rows whose kind is toggled off. */
const KEY = (uid: string) => `fig_notif_prefs_${uid}`;

export type NotificationPrefs = Record<NotificationKind, boolean>;

export const DEFAULT_PREFS: NotificationPrefs = {
  grail: true,
  message: true,
  comment: true,
  reaction: true,
  verification: true,
};

export function readPrefs(uid: string | null | undefined): NotificationPrefs {
  if (!uid) return DEFAULT_PREFS;
  try {
    const raw = localStorage.getItem(KEY(uid));
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<NotificationPrefs>) };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function writePrefs(uid: string, prefs: NotificationPrefs) {
  try {
    localStorage.setItem(KEY(uid), JSON.stringify(prefs));
    window.dispatchEvent(new CustomEvent("fig-notif-prefs", { detail: { uid } }));
  } catch {
    /* ignore */
  }
}

/** Reactive hook — re-renders when prefs change in the same tab. */
export function useNotificationPrefs(uid: string | null | undefined) {
  const [prefs, setPrefs] = useState<NotificationPrefs>(() => readPrefs(uid));
  useEffect(() => {
    setPrefs(readPrefs(uid));
    const onChange = () => setPrefs(readPrefs(uid));
    window.addEventListener("fig-notif-prefs", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("fig-notif-prefs", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [uid]);
  const set = (next: NotificationPrefs) => {
    if (uid) writePrefs(uid, next);
    setPrefs(next);
  };
  return { prefs, set };
}